/**
 * Persistence bridge between DocumentEditor and Convex entity storage.
 *
 * Connects the per-tab document model with the entity form system:
 * - Loads per-tab documents from serialized `documentJson` maps via runtime schema hydration
 * - Tracks current document JSON for the active tab
 * - Extracts form data for save operations from explicit tab documents only
 * - Detects dirty state via deep JSON equality
 *
 * This hook does NOT create the editor — it manages the data flow around it.
 * The consuming component uses DocumentEditor's `documentContent` and `onDocumentJson` props
 * to wire the editor to this hook.
 *
 * Usage:
 * ```tsx
 * const persistence = useDocumentPersistence({
 *   entityType: "dataset",
 *   activeTabKey: "dataset.definition",
 *   documentJson: entity?.documentJson,
 * });
 *
 * <DocumentEditor
 *   entityType="dataset"
 *   documentContent={persistence.documentContent}
 *   onDocumentJson={persistence.handleDocumentJsonChange}
 * />
 * ```
 */

import { createBlockId, readBlockId } from "@repo/shared";
import type {
  CitationRewrite,
  DocumentEvidenceLocatorFields,
} from "@repo/shared/document-evidence-link";
import type { JSONContent } from "@tiptap/react";
import { isEqual } from "lodash-es";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { canonicalizeRichTextTabDocument } from "../document-json-normalization";
import { BLOCK_ID_NODE_TYPES } from "../extensions/block-id-node-types";
import type { CompiledSchemaRegistry } from "../extensions/schemas";
import {
  buildCompiledSchemaRegistry,
  createTabInitialContent,
  extractTabFormData,
  hydrateTabDocument,
  resolveRuntimeEntityTabMap,
  resolveRuntimeTabSchema,
} from "../extensions/schemas";
import type {
  DocumentEntityType,
  DocumentSchemaConfig,
  ExtractedFormData,
} from "../extensions/schemas/types";

// ─── Types ───────────────────────────────────────────────────────────────────

/** Per-tab materialized document map used by the editor. Keys are tab keys. */
export type DocumentJsonMap = Record<string, JSONContent>;

/** Per-tab serialized document map used at the Convex persistence boundary. */
export type SerializedDocumentJsonMap = Record<string, string>;

export type PersistedDocumentJsonMap = Record<string, JSONContent | string>;

export interface UseDocumentPersistenceOptions {
  /** Entity type for schema lookup and form data extraction. */
  entityType: DocumentEntityType;
  /** Currently active framework tab key (e.g., "dataset.definition"). */
  activeTabKey: string | null;
  /** Per-tab document map from Convex (or null if none stored yet). */
  documentJson?: PersistedDocumentJsonMap | null;
  /**
   * Per-tab document map used as the dirty comparison baseline.
   * Defaults to `documentJson`. Pass the current published entity documents
   * when `documentJson` contains a recoverable draft working state.
   */
  dirtyBaselineDocumentJson?: PersistedDocumentJsonMap | null;
  /**
   * Preview document map from a version snapshot (read-only overlay).
   * When provided, `documentContent` resolves from this map instead of
   * `documentJson`/`localEdits`. Dirty tracking is unaffected — it
   * always compares the working state against the dirty baseline.
   */
  previewDocumentJson?: PersistedDocumentJsonMap | null;
  /** Framework entity type key (e.g., initiativeOverview, dataset) for runtime template maps. */
  frameworkEntityTypeKey?: string;
  /** Compiled template payload from frameworkStructureViews.compiledTemplateJson. */
  compiledTemplateJson?: import("@repo/shared").CompiledTemplateJson | null;
}

export interface UseDocumentPersistenceReturn {
  /** Content JSON for the active tab — pass to DocumentEditor's `documentContent` prop. */
  documentContent: JSONContent | null;
  /**
   * The whole document the editor is working on: stored content with local edits overlaid.
   *
   * This is the one map that is true about what is on screen, and it is reactive — unlike
   * `getMaterializedDocumentJsonForSave`, which reads a ref and so cannot be a render input. Any
   * feature that has to show *the editor's* content rather than the server's — version compare's
   * "Draft" side (LAT-938) — must read this rather than assembling its own copy from the draft
   * row plus a private edit buffer: a second copy is a second thing to keep current, and the one
   * that misses a write (a rebase replacing the local edits under it) reports the user reverting
   * work they never touched.
   */
  workingDocumentJson: DocumentJsonMap;
  /** Callback for DocumentEditor's `onDocumentJson` prop. */
  handleDocumentJsonChange: (
    json: JSONContent,
    meta?: { isFocused?: boolean; suppressDirtyTracking?: boolean }
  ) => void;
  /** Whether any tab document has changed from the stored version. */
  isDirty: boolean;
  /** Get the serialized full documentJson map (all tabs) for save operations. */
  getDocumentJsonForSave: () => SerializedDocumentJsonMap;
  /** Get the materialized full document map for local extraction work. */
  getMaterializedDocumentJsonForSave: () => DocumentJsonMap;
  /** Get merged form data from all tabs (for Markdown typed column writes). */
  getFormDataForSave: () => ExtractedFormData;
  /** Clear working editor overrides (used when form discard resets working state). */
  resetLocalEdits: () => void;
  /**
   * The working editor overrides themselves: only the tabs the user has actually typed into.
   *
   * Distinct from `getMaterializedDocumentJsonForSave`, which is the *complete* map — server
   * content with these overlaid. A caller rebasing the editor onto an arriving version needs to
   * know which tabs are being held locally, because those are the only ones masking that version
   * (LAT-938); handing the complete map back to `replaceLocalEdits` would make every tab a local
   * override and freeze the element against every later write.
   */
  getLocalEdits: () => DocumentJsonMap;
  /**
   * Replace working editor overrides with an explicit document map.
   * Useful for version recovery flows where edited state should match a snapshot.
   */
  replaceLocalEdits: (next: PersistedDocumentJsonMap) => void;
  /**
   * Mark the current working document state as saved.
   * Prevents transient stale-server dirty state immediately after save.
   *
   * With `saved`, the documents the save sent and the ones the server stored: the stored copy
   * becomes the baseline and the working copy of every tab not typed in since the save started,
   * so a save the server rewrote (a citation carried to a newer version, a renamed label) leaves
   * the editor on what was stored rather than on what was sent.
   */
  markSaved: (saved?: SavedDocuments) => void;
}

/** What one save sent, what the server stored for it, and the citations it stored elsewhere. */
export interface SavedDocuments {
  sent: DocumentJsonMap;
  stored: PersistedDocumentJsonMap;
  citationRewrites?: readonly CitationRewrite[];
}

const BLOCK_ID_ATTR = "blockId";

type EvidenceAttrs = Record<string, unknown>;

function evidenceLocatorKey(
  attrs: EvidenceAttrs | DocumentEvidenceLocatorFields | undefined
): string {
  const fields = attrs as EvidenceAttrs | undefined;
  return JSON.stringify([
    fields?.artifactId,
    fields?.processingRevisionId,
    fields?.blockId,
    fields?.startChar ?? null,
    fields?.endChar ?? null,
  ]);
}

/**
 * Records one rewrite, keeping every entry pointing at the latest locator: a later save that
 * moves `2 → 3` moves an earlier `1 → 2` to `1 → 3`, and an earlier `2 → 3` makes a new `1 → 2`
 * read `1 → 3`, so no stale copy can be carried to an intermediate place.
 */
function addCitationRewrite(
  rewrites: Map<string, DocumentEvidenceLocatorFields>,
  rewrite: CitationRewrite
) {
  const fromKey = evidenceLocatorKey(rewrite.from);
  const target = rewrites.get(evidenceLocatorKey(rewrite.to)) ?? rewrite.to;
  if (evidenceLocatorKey(target) === fromKey) return;
  for (const [key, existing] of rewrites) {
    if (evidenceLocatorKey(existing) === fromKey) rewrites.set(key, target);
  }
  rewrites.set(fromKey, target);
}

/**
 * `document` with every citation still naming a rewritten locator moved to where the save stored
 * it; the user's text and the citations they changed or added stay as they are. The same object
 * when nothing applies.
 */
function applyCitationRewrites(
  document: JSONContent,
  rewrites: ReadonlyMap<string, DocumentEvidenceLocatorFields>
): JSONContent {
  if (rewrites.size === 0) return document;
  const visit = (node: JSONContent): JSONContent => {
    if (node.type === "documentEvidence") {
      const target = rewrites.get(evidenceLocatorKey(node.attrs));
      if (!target) return node;
      const { startChar: _start, endChar: _end, ...attrs } = node.attrs ?? {};
      return { ...node, attrs: { ...attrs, ...target } };
    }
    if (!node.content) return node;
    const content = node.content.map(visit);
    return content.some((child, index) => child !== node.content![index])
      ? { ...node, content }
      : node;
  };
  return visit(document);
}
/** Same set the BlockIds extension registers — see `block-id-node-types.ts`. */
const BLOCK_ANCHOR_NODE_TYPES = new Set<string>(BLOCK_ID_NODE_TYPES);

function isJsonContentArray(value: unknown): value is JSONContent[] {
  return Array.isArray(value);
}

type EnsureBlockIdsResult = {
  doc: JSONContent;
  changed: boolean;
};

export function ensureBlockIdsInDocumentJson(doc: JSONContent): EnsureBlockIdsResult {
  const seen = new Set<string>();

  const normalizeNode = (node: JSONContent): { node: JSONContent; changed: boolean } => {
    let changed = false;
    let nextAttrs = node.attrs;

    if (typeof node.type === "string" && BLOCK_ANCHOR_NODE_TYPES.has(node.type)) {
      const currentId = readBlockId(node.attrs?.[BLOCK_ID_ATTR]);
      const nextId = currentId && !seen.has(currentId) ? currentId : createBlockId();
      seen.add(nextId);

      if (nextId !== currentId) {
        nextAttrs = {
          ...(node.attrs ?? {}),
          [BLOCK_ID_ATTR]: nextId,
        };
        changed = true;
      }
    }

    if (!isJsonContentArray(node.content)) {
      if (!changed) {
        return { node, changed: false };
      }
      return {
        node: {
          ...node,
          ...(nextAttrs ? { attrs: nextAttrs } : {}),
        },
        changed: true,
      };
    }

    let childChanged = false;
    const nextContent = node.content.map((child) => {
      const result = normalizeNode(child);
      if (result.changed) childChanged = true;
      return result.node;
    });

    if (!changed && !childChanged) {
      return { node, changed: false };
    }

    return {
      node: {
        ...node,
        ...(nextAttrs ? { attrs: nextAttrs } : {}),
        ...(childChanged ? { content: nextContent } : {}),
      },
      changed: true,
    };
  };

  const normalized = normalizeNode(doc);
  return {
    doc: normalized.node,
    changed: normalized.changed,
  };
}

/**
 * The one comparison that decides whether a tab holds unsaved work.
 *
 * The two sides are never the same shape. One is what the server stored; the other is the editor's
 * own copy, which has been hydrated against the template, had block ids injected, and picked up
 * whatever the runtime decorates it with. None of that is authored content, so none of it may make
 * a document count as an edit — and the list of what to normalize is not a matter of taste: it is
 * everything the persistence canonicalizer removes on the way out
 * (`canonicalizeDocumentJsonForSnapshotPersistence`), because that is the definition of what does
 * not get stored.
 *
 * Clarification anchor marks were the one this missed (LAT-938 round 3), and the gap is expensive
 * out of all proportion to its size. Anchors are applied while rendering and stripped before
 * persistence, so the editor's copy of a tab it had just saved never compared equal to the stored
 * document — which made an editor holding nothing read dirty, took a clean session down the merge
 * path instead of simply following the server, and had autosave write the buffer out as a draft.
 * The row it produced was byte-identical to the version the user had already saved, because the
 * snapshot on its way out went through the canonicalizer this one had drifted from. A draft of
 * nothing, standing in front of every version that came next.
 */
function canonicalizeTabDocumentForDirtyState(
  schemaConfig: DocumentSchemaConfig,
  document: JSONContent
): JSONContent {
  return canonicalizeRichTextTabDocument(document, {
    schemaConfig,
    stripBlockIds: true,
    stripClarificationAnchorMarks: true,
    stripOrderedListDefaults: true,
    stripEmptyTopLevelFields: true,
  });
}

function areTabDocumentsEquivalentForDirtyState(
  tabKey: string,
  localDoc: JSONContent,
  storedDoc: JSONContent | null,
  compiledSchemaRegistry?: CompiledSchemaRegistry | null
): boolean {
  if (storedDoc && isEqual(localDoc, storedDoc)) return true;
  if (!storedDoc) return false;

  const tabSchema = resolveRuntimeTabSchema(tabKey, { compiledSchemaRegistry });
  const canonicalLocalDoc = canonicalizeTabDocumentForDirtyState(tabSchema, localDoc);
  const canonicalStoredDoc = canonicalizeTabDocumentForDirtyState(tabSchema, storedDoc);
  return isEqual(canonicalLocalDoc, canonicalStoredDoc);
}

function hasMeaningfulFieldValue(value: unknown): boolean {
  if (value === undefined || value === null) return false;
  if (typeof value === "string") return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  return true;
}

function hasMeaningfulTabData(data: ExtractedFormData): boolean {
  return Object.values(data).some((value) => hasMeaningfulFieldValue(value));
}

function requireFrameworkEntityTypeKey(frameworkEntityTypeKey: string | undefined): string {
  if (frameworkEntityTypeKey) {
    return frameworkEntityTypeKey;
  }

  throw new Error("Missing framework entity type key for document persistence runtime.");
}

export function materializeDocumentJsonMap(
  value: PersistedDocumentJsonMap | null | undefined
): DocumentJsonMap | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }

  const result: DocumentJsonMap = {};
  for (const [tabKey, document] of Object.entries(value)) {
    if (typeof document === "string") {
      result[tabKey] = JSON.parse(document) as JSONContent;
      continue;
    }
    result[tabKey] = document;
  }
  return result;
}

export function serializeDocumentJsonMapForPersistence(
  value: DocumentJsonMap | null | undefined
): SerializedDocumentJsonMap {
  if (!value) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(value).map(([tabKey, document]) => [tabKey, JSON.stringify(document)])
  );
}

// ─── Hook ────────────────────────────────────────────────────────────────────

export function useDocumentPersistence({
  entityType,
  frameworkEntityTypeKey,
  compiledTemplateJson,
  activeTabKey,
  documentJson,
  dirtyBaselineDocumentJson,
  previewDocumentJson,
}: UseDocumentPersistenceOptions): UseDocumentPersistenceReturn {
  const materializedDocumentJson = useMemo(
    () => materializeDocumentJsonMap(documentJson),
    [documentJson]
  );
  const materializedDirtyBaselineDocumentJson = useMemo(
    () => materializeDocumentJsonMap(dirtyBaselineDocumentJson),
    [dirtyBaselineDocumentJson]
  );
  const materializedPreviewDocumentJson = useMemo(
    () => materializeDocumentJsonMap(previewDocumentJson),
    [previewDocumentJson]
  );
  const compiledSchemaRegistry = useMemo(
    () => buildCompiledSchemaRegistry(compiledTemplateJson),
    [compiledTemplateJson]
  );
  const resolveTabDocument = useCallback(
    (tabKey: string | null, source?: DocumentJsonMap | null): JSONContent | null => {
      if (!tabKey) {
        return null;
      }

      const schema = resolveRuntimeTabSchema(tabKey, { compiledSchemaRegistry });

      if (source && tabKey in source) {
        return hydrateTabDocument(schema, source[tabKey] as JSONContent);
      }

      return createTabInitialContent(schema);
    },
    [compiledSchemaRegistry]
  );

  // Track local edits per tab (overrides stored content during editing)
  const [localEdits, setLocalEdits] = useState<DocumentJsonMap>({});
  /** Citations saves of this session rewrote, by the locator they were sent with. */
  const citationRewritesRef = useRef(new Map<string, DocumentEvidenceLocatorFields>());
  // Local baseline used after a successful save until server data catches up.
  const [savedBaseline, setSavedBaseline] = useState<DocumentJsonMap | null>(null);

  /**
   * The overrides that are still saying something the stored document does not (LAT-938 round 2).
   *
   * An override outranks stored content unconditionally, which is right for a tab the user is
   * typing into and wrong for a tab that merely holds a *copy* of what the server already says.
   * Copies do get installed: a rebase that keeps nothing of the user's rebuilds a tab into exactly
   * the arriving version, and an editor can echo back content it was just handed. Left in place, a
   * copy goes on outranking stored content after the server has moved past it — the element
   * advances, the editor stays frozen on the version the copy captured, and the form then reads
   * itself dirty against a version it never showed and autosaves that as a draft nobody wrote.
   * That is the phantom draft holding the previous version's content, and the stuck editor behind
   * it.
   *
   * So a copy stops counting the moment it is one, by the same canonicalized comparison that
   * decides dirty state. Derived during render rather than pruned from an effect: an override that
   * outranks stored content for even one commit is an override that can be autosaved.
   */
  const effectiveLocalEdits = useMemo<DocumentJsonMap>(() => {
    const tabKeys = Object.keys(localEdits);
    if (tabKeys.length === 0) return localEdits;

    const kept: DocumentJsonMap = {};
    for (const tabKey of tabKeys) {
      const localDoc = localEdits[tabKey];
      if (!localDoc) continue;
      // Both sides hydrated, exactly as the dirty check below does it. Comparing a raw override
      // against a hydrated stored document asks whether two *shapes* match rather than whether two
      // documents say the same thing, and the answer is always no — which would make this rule fire
      // for nothing and quietly keep every copy.
      const workingDoc = resolveTabDocument(tabKey, { [tabKey]: localDoc });
      const storedDoc = resolveTabDocument(tabKey, materializedDocumentJson ?? undefined);
      if (
        !workingDoc ||
        !areTabDocumentsEquivalentForDirtyState(
          tabKey,
          workingDoc,
          storedDoc,
          compiledSchemaRegistry
        )
      ) {
        kept[tabKey] = localDoc;
      }
    }
    return Object.keys(kept).length === tabKeys.length ? localEdits : kept;
  }, [compiledSchemaRegistry, localEdits, materializedDocumentJson, resolveTabDocument]);

  const localEditsRef = useRef(effectiveLocalEdits);
  localEditsRef.current = effectiveLocalEdits;

  // And it has to stick. Deriving alone only hides the copy while the stored document still
  // matches it; the moment the server moves on, the raw state would answer again as an override of
  // content it predates. Committing the pruning is what actually lets the tab follow the server.
  useEffect(() => {
    if (effectiveLocalEdits !== localEdits) {
      setLocalEdits(effectiveLocalEdits);
    }
  }, [effectiveLocalEdits, localEdits]);

  // Resolve content for the active tab
  const documentContent = useMemo(() => {
    if (!activeTabKey) {
      return null;
    }
    // Preview mode: resolve from snapshot (read-only overlay)
    if (materializedPreviewDocumentJson) {
      return resolveTabDocument(activeTabKey, materializedPreviewDocumentJson);
    }
    // Local edit takes precedence over stored content
    if (activeTabKey in effectiveLocalEdits) {
      return effectiveLocalEdits[activeTabKey] ?? null;
    }
    return resolveTabDocument(activeTabKey, materializedDocumentJson ?? undefined);
  }, [
    activeTabKey,
    effectiveLocalEdits,
    materializedDocumentJson,
    materializedPreviewDocumentJson,
    resolveTabDocument,
  ]);

  // Handle document JSON changes from the editor
  const handleDocumentJsonChange = useCallback(
    (json: JSONContent, meta?: { isFocused?: boolean; suppressDirtyTracking?: boolean }) => {
      // Ignore editor updates while previewing historical versions.
      // Some editor lifecycle emissions can occur without real user edits.
      if (materializedPreviewDocumentJson) return;
      if (meta?.suppressDirtyTracking) return;
      if (!activeTabKey) return;

      const currentDoc =
        activeTabKey in localEditsRef.current
          ? (localEditsRef.current[activeTabKey] ?? null)
          : resolveTabDocument(activeTabKey, materializedDocumentJson ?? undefined);

      // An editor that kept focus through a save can still hold citations the save rewrote; what
      // it emits takes the stored ones, so a keystroke cannot send them back.
      const normalizedNextDoc = applyCitationRewrites(
        ensureBlockIdsInDocumentJson(json).doc,
        citationRewritesRef.current
      );

      // Ignore no-op updates to avoid phantom dirty toggles.
      if (isEqual(normalizedNextDoc, currentDoc)) return;

      // Some editor lifecycle updates can emit structurally different JSON that
      // maps to the same tab form data. Treat those as no-ops as well.
      const tabSchema = resolveRuntimeTabSchema(activeTabKey, { compiledSchemaRegistry });
      if (currentDoc) {
        const nextTabData = extractTabFormData(tabSchema, normalizedNextDoc);
        const currentTabData = extractTabFormData(tabSchema, currentDoc);
        // Guard against unfocused mount/hydration transactions that occasionally
        // emit empty docs and would otherwise clear loaded content + mark dirty.
        if (
          meta?.isFocused === false &&
          hasMeaningfulTabData(currentTabData) &&
          !hasMeaningfulTabData(nextTabData)
        ) {
          return;
        }
        if (
          isEqual(nextTabData, currentTabData) &&
          areTabDocumentsEquivalentForDirtyState(
            activeTabKey,
            normalizedNextDoc,
            currentDoc,
            compiledSchemaRegistry
          )
        ) {
          return;
        }
      }

      setLocalEdits((prev) => ({
        ...prev,
        [activeTabKey]: normalizedNextDoc,
      }));
    },
    [
      activeTabKey,
      materializedDocumentJson,
      materializedPreviewDocumentJson,
      resolveTabDocument,
      compiledSchemaRegistry,
    ]
  );

  const resetLocalEdits = useCallback(() => {
    setLocalEdits({});
    setSavedBaseline(null);
  }, []);

  // Read off the ref rather than the state so a caller reading it inside an effect sees what the
  // editor is holding *now*, not what it was holding when the callback was last built.
  const getLocalEdits = useCallback((): DocumentJsonMap => ({ ...localEditsRef.current }), []);

  const replaceLocalEdits = useCallback((next: PersistedDocumentJsonMap) => {
    setLocalEdits(materializeDocumentJsonMap(next) ?? {});
    setSavedBaseline(null);
  }, []);

  const workingDocumentJson = useMemo<DocumentJsonMap>(
    () => ({
      ...(materializedDocumentJson ?? {}),
      ...effectiveLocalEdits,
    }),
    [materializedDocumentJson, effectiveLocalEdits]
  );

  // Dirty detection: compare the effective working document map against the publish baseline.
  const isDirty = useMemo(() => {
    const workingTabKeys = Object.keys(workingDocumentJson);
    if (workingTabKeys.length === 0) return false;
    const baseline =
      savedBaseline ??
      materializedDirtyBaselineDocumentJson ??
      materializedDocumentJson ??
      undefined;

    for (const tabKey of workingTabKeys) {
      const workingDoc = resolveTabDocument(tabKey, workingDocumentJson);
      const storedDoc = resolveTabDocument(tabKey, baseline);
      if (
        !workingDoc ||
        !areTabDocumentsEquivalentForDirtyState(
          tabKey,
          workingDoc,
          storedDoc,
          compiledSchemaRegistry
        )
      ) {
        return true;
      }
    }
    return false;
  }, [
    workingDocumentJson,
    savedBaseline,
    materializedDirtyBaselineDocumentJson,
    materializedDocumentJson,
    resolveTabDocument,
    compiledSchemaRegistry,
  ]);

  // Build the full documentJson map for save operations
  const getMaterializedDocumentJsonForSave = useCallback((): DocumentJsonMap => {
    const result: DocumentJsonMap = { ...(materializedDocumentJson ?? {}) };
    const resolvedFrameworkEntityTypeKey = requireFrameworkEntityTypeKey(frameworkEntityTypeKey);

    // Overlay local edits
    for (const [tabKey, localDoc] of Object.entries(localEditsRef.current)) {
      result[tabKey] = localDoc;
    }

    // Ensure all tabs have content (fill empty tabs with default)
    const tabMap = resolveRuntimeEntityTabMap(entityType, {
      frameworkEntityTypeKey: resolvedFrameworkEntityTypeKey,
      compiledSchemaRegistry,
    });
    for (const tabKey of Object.keys(tabMap)) {
      if (!(tabKey in result)) {
        const resolved = resolveTabDocument(tabKey, null);
        if (resolved) result[tabKey] = resolved;
      }
    }

    for (const [tabKey, tabDoc] of Object.entries(result)) {
      const { doc: normalizedDoc } = ensureBlockIdsInDocumentJson(tabDoc);
      result[tabKey] = normalizedDoc;
    }

    return result;
  }, [
    entityType,
    materializedDocumentJson,
    frameworkEntityTypeKey,
    compiledSchemaRegistry,
    resolveTabDocument,
  ]);

  const getDocumentJsonForSave = useCallback(
    (): SerializedDocumentJsonMap =>
      serializeDocumentJsonMapForPersistence(getMaterializedDocumentJsonForSave()),
    [getMaterializedDocumentJsonForSave]
  );

  // Extract merged form data from all tabs for Markdown column writes
  const getFormDataForSave = useCallback((): ExtractedFormData => {
    const fullDocMap = getMaterializedDocumentJsonForSave();
    const resolvedFrameworkEntityTypeKey = requireFrameworkEntityTypeKey(frameworkEntityTypeKey);
    const tabMap = resolveRuntimeEntityTabMap(entityType, {
      frameworkEntityTypeKey: resolvedFrameworkEntityTypeKey,
      compiledSchemaRegistry,
    });
    const extracted: ExtractedFormData = {};
    for (const [tabKey, schema] of Object.entries(tabMap)) {
      const doc = fullDocMap[tabKey];
      if (!doc?.content) continue;
      Object.assign(extracted, extractTabFormData(schema, doc));
    }

    return extracted;
  }, [
    entityType,
    getMaterializedDocumentJsonForSave,
    frameworkEntityTypeKey,
    compiledSchemaRegistry,
  ]);

  const markSaved = useCallback(
    (saved?: SavedDocuments) => {
      const stored = materializeDocumentJsonMap(saved?.stored);
      if (!saved || !stored) {
        // Capture the exact map that was persisted so dirty checks don't immediately
        // re-trigger drafts while waiting for Convex query refresh.
        setSavedBaseline(getMaterializedDocumentJsonForSave());
        return;
      }
      // A tab still holding exactly what was sent takes what was stored. A tab typed in while the
      // save was in flight keeps the user's edit, and reads dirty against the stored copy, but
      // takes the citations the save reports storing elsewhere: that save superseded what would
      // move them again, so the next save must not send the old ones back.
      for (const rewrite of saved.citationRewrites ?? []) {
        addCitationRewrite(citationRewritesRef.current, rewrite);
      }
      setLocalEdits((current) => {
        let next = current;
        for (const [tabKey, document] of Object.entries(current)) {
          const storedDocument = stored[tabKey];
          if (!storedDocument) continue;
          const adopted = isEqual(document, saved.sent[tabKey])
            ? storedDocument
            : applyCitationRewrites(document, citationRewritesRef.current);
          if (adopted === document) continue;
          if (next === current) next = { ...current };
          next[tabKey] = adopted;
        }
        return next;
      });
      setSavedBaseline(stored);
    },
    [getMaterializedDocumentJsonForSave]
  );

  useEffect(() => {
    if (!savedBaseline) return;

    // Clear optimistic baseline once server catches up.
    if (
      isEqual(
        savedBaseline,
        materializedDirtyBaselineDocumentJson ?? materializedDocumentJson ?? {}
      )
    ) {
      setSavedBaseline(null);
    }
  }, [savedBaseline, materializedDirtyBaselineDocumentJson, materializedDocumentJson]);

  return {
    documentContent,
    workingDocumentJson,
    handleDocumentJsonChange,
    isDirty,
    getDocumentJsonForSave,
    getMaterializedDocumentJsonForSave,
    getFormDataForSave,
    resetLocalEdits,
    getLocalEdits,
    replaceLocalEdits,
    markSaved,
  };
}

export const __testOnly = {
  areTabDocumentsEquivalentForDirtyState,
};
